import React, { useState, useEffect, useCallback, useRef } from 'react'
import { useSelector, useDispatch } from 'react-redux';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Row, Col, Input, Button, Form } from 'reactstrap';
import { TransitionGroup, CSSTransition } from "react-transition-group";
import { teamActions, getCurrentTeam } from '../../../core/teams'
import { DEFAULT_SESSION_ID } from '../../../core/constants'
import { teamsReportListActions, getTeamsReportById } from '../../../core/teams-report'
import { getCurrentMembersList, getMembersForCurrentMembersList, membersListActions } from '../../../core/members-list';
import LoadingIndicator from '../../components/loading-indicator'
import icon_members from '../../../assets/img/member-avatar.svg';
import icon_avatar from '../../../assets/img/edit-avatar.svg';
import { MemberItem } from '../../components/teams';
import ReactGA from 'react-ga'
import { trackingActions } from '../../../core/tracking';

const EditPage = () => {
  const { id } = useParams();
  const team_id = id || DEFAULT_SESSION_ID;
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const fileInputRef = useRef(null);
  const nodeRef = useRef(null);

  const user = useSelector(state => state.user);
  const team = useSelector(state => getCurrentTeam(state));
  const team_report = useSelector(state => getTeamsReportById(state, team_id));
  const members_list = useSelector(state => getCurrentMembersList(state));
  const members = useSelector(state => getMembersForCurrentMembersList(state));

  const [animationClass] = useState({
    appearActive: 'animate__animated animate__slideInRight position-fixed',
    enter: 'animate__animated',
    enterActive: 'animate__animated animate__slideInRight position-fixed',
    exit: 'animate__animated',
    exitActive: 'animate__animated animate__slideOutLeft position-fixed',
  });
  
  const [localTeam, setLocalTeam] = useState(team ? team.toJS() : {});
  const [teamImage, setTeamImage] = useState(null);
  const [teamImagePreview, setTeamImagePreview] = useState(team?.image || icon_members);
  const [currentAction, setCurrentAction] = useState(null);
  const [validation, setValidation] = useState({
    message: null,
    valid: true
  });
  const [searchMembers, setSearchMembers] = useState('');
  const [filteredMembers, setFilteredMembers] = useState(members);

  const displayLoadingIndicator = (members_list ? members_list.isPending : false) || (team ? team.isPending : false) || (team_report ? team_report.isPending : false) || false;

  useEffect(() => {
    if (!team || (team.id !== team_id)) {
      dispatch(teamActions.loadTeam(team_id));
    }

    if (!members || members.size === 0) {
      handleSearchMembers();
    }

    if (team_id !== DEFAULT_SESSION_ID && !team.name) {
      dispatch(teamsReportListActions.loadTeamsReport(team_id));
    }
    
    if (window.location.pathname === '/teams/create') {
      setLocalTeam(team_report.toJS());
    }
  }, [dispatch, team_id, team, team_report, members]);

  useEffect(() => {
    if (team_report && team_report.isNew && team_report.isPending && !team_report.isPending) {
      setLocalTeam(team_report.toJS());
    }
  }, [team_report]);

  useEffect(() => {
    if (members_list.isPending && !members_list.isPending && members.size !== members.size) {
      setFilteredMembers(members);
    }
  }, [members_list, members]);

  useEffect(() => {
    filterMembersLocally();
  }, [searchMembers, members]);

  useEffect(() => {
    if (team.update_at !== team.update_at) {
      if (team.error) {
        let newValidation = {
          message: team.error.message,
          valid: false
        };
        
        if (team.error.code === 'TEA0004') {
          newValidation = {
            message: 'There is already a team with that name.',
            valid: false
          };
        }

        setValidation(newValidation);
      } else {
        if (currentAction === 'create') {
          dispatch(teamActions.fetchTeamDefaultPendingFalse());
          handleSearchMembers();
          setLocalTeam(team_report.toJS());
          navigate(`/teams/${team.id}`);
        } else if (currentAction) {
          navigate(`/teams/${team.id}`);
        }
      }
    }
  }, [team, currentAction, dispatch, navigate, team_report]);

  const filterMembersLocally = useCallback(() => {
    const formattedSearch = searchMembers.toLowerCase().trim();

    const filtered = members.filter(member => {
      const firstName = member.first_name.toLowerCase();
      const lastName = member.last_name.toLowerCase();
      const email = member.email.toLowerCase();
      const fullName = firstName + ' ' + lastName;

      const fullNameIncludeSearch = fullName.includes(formattedSearch);
      const includeEmail = email.includes(formattedSearch);

      return fullNameIncludeSearch || includeEmail;
    });

    setFilteredMembers(filtered);
  }, [searchMembers, members]);

  const handleSearchMembers = useCallback(() => {
    dispatch(membersListActions.findMembers({ team_id }));
  }, [dispatch, team_id]);

  const onChangeLeaderMember = useCallback(() => {
    dispatch(teamsReportListActions.loadTeamsReport(team_id));
  }, [dispatch, team_id]);

  const handleImageChange = useCallback((e) => {
    const file = e.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onloadend = () => {
        setTeamImage(file);
        setTeamImagePreview(reader.result);
      };
      reader.readAsDataURL(file);
    }
  }, []);

  const handleInputChange = useCallback((event) => {
    const target = event.target;
    const value = target.type === 'checkbox' ? target.checked : target.value;
    const name = target.name;

    if (name === 'search_members') {
      setSearchMembers(value);
    } else {
      setLocalTeam(prevTeam => ({
        ...prevTeam,
        [name]: value.trim()
      }));
      
      setValidation({
        message: null,
        valid: true
      });
    }
  }, []);

  const handleClick = useCallback(({ action = 'save' }) => {
    if (!localTeam.name || !localTeam.name.length) {
      setValidation({
        message: 'Please enter a team name.',
        valid: false
      });
      return;
    }
    
    if (action === 'save') {
      if (localTeam.id === DEFAULT_SESSION_ID) {
        ReactGA.event({
          category: 'Teams',
          action: 'Creation completed'
        });
        
        dispatch(trackingActions.saveTrackingEvent({ category: 'Teams', action: 'Creation completed' }));
        dispatch(teamActions.createTeam(localTeam));
        setCurrentAction('create');
      } else {
        dispatch(teamActions.updateTeam({ ...localTeam, image: teamImage }));
        setCurrentAction('update');
      }
    } else {
      navigate(-1);
    }
  }, [dispatch, localTeam, teamImage, navigate]);

  const handleBack = useCallback((e) => {
    e.preventDefault();
    navigate(-1);
  }, [navigate]);

  // Renderizado condicional
  if (user.checked && !user.id) {
    return null;
  }

  const msg_error = (!validation.valid) && (
    <p className="line-height-normal mb-0">
      <small className='text-danger'>{validation.message}</small>
    </p>
  );

  return (
    <>
      <div className="container-fluid h-100">
        <div className="view team-content h-100">
          <TransitionGroup>
            <CSSTransition nodeRef={nodeRef} classNames={animationClass} timeout={{ enter: 1000, exit: 1000 }} key='teams-edit' appear>
              <div ref={nodeRef} className="w-100 h-100 d-flex flex-column">
                <div className='mb-3 pt-md-4'>
                  <h1 className='mb-2 mb-lg-0 libre-caslon-text-regular h3-md h4'>Teams</h1>
                </div>

                <div className="position-relative d-flex justify-content-between align-items-center mb-3">
                  <Button color='link' onClick={handleBack} className="p-0 text-body mr-2">
                    <svg xmlns="http://www.w3.org/2000/svg" width={28} height={29} viewBox="0 0 28 29" fill='currentColor'>
                      <path d="M24.5,14.5c0,0.2-0.1,0.5-0.3,0.6c-0.2,0.2-0.4,0.3-0.6,0.3H6.5l6.4,6.4c0.1,0.1,0.1,0.2,0.2,0.3
                      c0,0.1,0.1,0.2,0.1,0.3s0,0.2-0.1,0.3c0,0.1-0.1,0.2-0.2,0.3c-0.1,0.1-0.2,0.1-0.3,0.2c-0.1,0-0.2,0.1-0.3,0.1c-0.1,0-0.2,0-0.3-0.1
                      c-0.1,0-0.2-0.1-0.3-0.2l-7.9-7.9c-0.1-0.1-0.1-0.2-0.2-0.3c0-0.1-0.1-0.2-0.1-0.3c0-0.1,0-0.2,0.1-0.3c0-0.1,0.1-0.2,0.2-0.3
                      L11.6,6c0.2-0.2,0.4-0.3,0.6-0.3c0.2,0,0.5,0.1,0.6,0.3c0.2,0.2,0.3,0.4,0.3,0.6c0,0.2-0.1,0.5-0.3,0.6l-6.4,6.4h17.1
                      c0.2,0,0.5,0.1,0.6,0.3C24.4,14,24.5,14.3,24.5,14.5z"/>
                    </svg>
                  </Button>

                  <div className="flex-grow-1">
                    <h2 className="mb-0 h4 h3-md">Edit {localTeam.name}</h2>
                  </div>

                  <div className='ml-auto botones-fixed border-top border-md-0 d-flex'>
                    <Button color='green-light' size='sm' className='text-nowrap mx-2' onClick={(e) => { e.preventDefault(); handleClick({ action: 'save' }) }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width={14} height={14} viewBox="0 0 14 14" fill='currentColor' className='mr-1 mt-n1'>
                        <path d="M12.7,4.4l-7,7c-0.1,0.1-0.1,0.1-0.2,0.1c-0.1,0-0.2,0.1-0.3,0.1s-0.2,0-0.3-0.1c-0.1,0-0.2-0.1-0.2-0.1
                        L1.7,8.3C1.7,8.3,1.6,8.2,1.6,8.1C1.5,8,1.5,8,1.5,7.9c0-0.1,0-0.2,0.1-0.3c0-0.1,0.1-0.2,0.1-0.2c0.1-0.1,0.1-0.1,0.2-0.1
                        c0.1,0,0.2-0.1,0.3-0.1c0.1,0,0.2,0,0.3,0.1c0.1,0,0.2,0.1,0.2,0.1L5.2,10l6.5-6.5c0.1-0.1,0.3-0.2,0.5-0.2c0.2,0,0.3,0.1,0.5,0.2
                        c0.1,0.1,0.2,0.3,0.2,0.5C12.9,4.1,12.8,4.3,12.7,4.4L12.7,4.4z"/>
                      </svg>
                      Save Changes
                    </Button>
                    <Button color='outline-green-light' size='sm' className="text-nowrap" onClick={(e) => { e.preventDefault(); handleClick({ action: 'cancel' }) }}>
                      <svg xmlns="http://www.w3.org/2000/svg" width={14} height={14} viewBox="0 0 14 14" fill='currentColor' className='mr-1 mt-n1'>
                        <path d="M11.4,10.5c0.1,0.1,0.2,0.3,0.2,0.5c0,0.2-0.1,0.3-0.2,0.5c-0.1,0.1-0.3,0.2-0.5,0.2c-0.2,0-0.3-0.1-0.5-0.2
                        L7,7.9l-3.5,3.5c-0.1,0.1-0.3,0.2-0.5,0.2s-0.3-0.1-0.5-0.2c-0.1-0.1-0.2-0.3-0.2-0.5c0-0.2,0.1-0.3,0.2-0.5L6.1,7L2.6,3.5
                        C2.5,3.4,2.4,3.2,2.4,3.1s0.1-0.3,0.2-0.5c0.1-0.1,0.3-0.2,0.5-0.2s0.3,0.1,0.5,0.2L7,6.1l3.5-3.5c0.1-0.1,0.3-0.2,0.5-0.2
                        c0.2,0,0.3,0.1,0.5,0.2c0.1,0.1,0.2,0.3,0.2,0.5s-0.1,0.3-0.2,0.5L7.9,7L11.4,10.5z"/>
                      </svg>
                      Discard Changes
                    </Button>
                  </div>
                </div>

                <Row className='border-bottom pb-3'>
                  <Col lg='6'>
                    <div className='d-flex'>
                      <div className='avatar-edit position-relative mr-4'>
                        <Button
                          color='link'
                          className='p-0 border-0'
                          onClick={() => fileInputRef.current.click()}
                        >
                          <img
                            className='avatar-edit rounded-circle'
                            width={80}
                            height={80}
                            src={teamImagePreview || localTeam.image}
                            alt={localTeam.name}
                          />
                          <div className='icon-edit'>
                            <img src={icon_avatar} width={24} height={24} alt='edit' />
                          </div>
                        </Button>
                        <input
                          type="file"
                          ref={fileInputRef}
                          style={{ display: 'none' }}
                          accept="image/*"
                          onChange={handleImageChange}
                        />
                      </div>
                      <div className='flex-fill'>
                        <form>
                          <div className="form-group mb-0">
                            <label htmlFor="inputEmail" className='text-muted'>Team Name</label>
                            <Input 
                              type="text" 
                              name="name" 
                              maxLength="30" 
                              onChange={handleInputChange} 
                              value={localTeam.name || ''} 
                              className="form-control" 
                              placeholder="Enter Team Name" 
                              bsSize="lg" 
                            />
                            {msg_error}
                          </div>
                        </form>
                      </div>
                    </div>
                  </Col>
                </Row>

                <div className='mb-3 py-3 d-flex justify-content-between'>
                  <div className='flex-fill mw-md-50 pr-2 pr-md-0'>
                    <Form onSubmit={e => { e.preventDefault() }}>
                      <div className="search-member-input rounded border d-flex align-items-center">
                        <Button color='link' className='text-muted p-0' >
                          <svg xmlns="http://www.w3.org/2000/svg" width={20} height={21} viewBox="0 0 20 21" fill='currentColor'>
                            <g>
                              <path d="M18.1,17.4L18.1,17.4L18.1,17.4c0.1,0.1,0.1,0.2,0.2,0.3c0,0.1,0.1,0.2,0.1,0.3c0,0.1,0,0.2-0.1,0.3
                                          c0,0.1-0.1,0.2-0.2,0.3s-0.2,0.1-0.3,0.2c-0.1,0-0.2,0.1-0.3,0.1c-0.1,0-0.2,0-0.3-0.1c-0.1,0-0.2-0.1-0.3-0.2l-3.7-3.7l0,0l0,0
                                          c-1.4,1.1-3.2,1.6-5,1.4c-1.8-0.2-3.5-1-4.7-2.4C2.2,12.6,1.6,10.8,1.6,9c0.1-1.8,0.8-3.5,2.1-4.8c1.3-1.3,3-2,4.8-2.1
                                          c1.8-0.1,3.6,0.6,4.9,1.8c1.4,1.2,2.2,2.9,2.4,4.7c0.2,1.8-0.3,3.6-1.4,5l0,0l0,0L18.1,17.4z M3.4,9.3L3.4,9.3
                                          C3.4,10.7,3.9,12,5,13c1,1,2.4,1.6,3.8,1.6h0c1.1,0,2.1-0.3,3-0.9c0.9-0.6,1.6-1.4,2-2.4c0.4-1,0.5-2.1,0.3-3.1
                                          c-0.2-1-0.7-2-1.5-2.8S10.8,4.2,9.8,4c-1-0.2-2.1-0.1-3.1,0.3c-1,0.4-1.8,1.1-2.4,2C3.7,7.1,3.4,8.2,3.4,9.3z"/>
                              <path d="M17.5,18.9c-0.1,0-0.2,0-0.4-0.1c-0.1,0-0.2-0.1-0.3-0.2L13.1,15c-1.5,1.1-3.3,1.6-5.1,1.5
                                          c-1.8-0.2-3.5-1-4.7-2.4c-1.2-1.4-1.8-3.1-1.8-5c0.1-1.8,0.8-3.6,2.1-4.9c1.3-1.3,3-2,4.9-2.1c1.8-0.1,3.6,0.6,5,1.8
                                          c1.4,1.2,2.2,2.9,2.4,4.7c0.2,1.8-0.3,3.6-1.5,5.1l3.8,3.8l0,0c0.1,0.1,0.1,0.2,0.2,0.3c0,0.1,0.1,0.2,0.1,0.4s0,0.2-0.1,0.4
                                          s-0.1,0.2-0.2,0.3c-0.1,0.1-0.2,0.2-0.3,0.2C17.7,18.9,17.6,18.9,17.5,18.9z M13.1,14.8L13.1,14.8l3.8,3.8c0.1,0.1,0.2,0.1,0.3,0.2
                                          c0.2,0.1,0.4,0.1,0.6,0c0.1,0,0.2-0.1,0.3-0.2c0.1-0.1,0.1-0.2,0.2-0.3c0-0.1,0.1-0.2,0.1-0.3c0-0.1,0-0.2-0.1-0.3
                                          c0-0.1-0.1-0.2-0.2-0.3l-0.1,0l0,0l-3.7-3.7l0.1-0.1c1.1-1.4,1.6-3.2,1.4-5c-0.2-1.8-1-3.4-2.4-4.6s-3.1-1.8-4.9-1.8
                                          C6.7,2.2,5,3,3.7,4.2c-1.3,1.3-2,3-2.1,4.8c-0.1,1.8,0.6,3.5,1.8,4.9c1.2,1.4,2.8,2.2,4.6,2.4C9.8,16.5,11.6,16,13.1,14.8
                                          L13.1,14.8z M8.8,14.7c-1.5,0-2.8-0.6-3.8-1.6c-1-1-1.6-2.4-1.6-3.8l0,0c0-1.1,0.3-2.1,0.9-3c0.6-0.9,1.4-1.6,2.4-2
                                          c1-0.4,2.1-0.5,3.1-0.3c1.1,0.2,2,0.7,2.8,1.5c0.8,0.8,1.3,1.7,1.5,2.8c0.2,1.1,0.1,2.1-0.3,3.1c-0.4,1-1.1,1.8-2,2.4
                                          C10.9,14.4,9.8,14.7,8.8,14.7z M3.4,9.3C3.4,10.7,4,12,5,13c1,1,2.3,1.6,3.8,1.6c1.1,0,2.1-0.3,3-0.9c0.9-0.6,1.6-1.4,2-2.4
                                          c0.4-1,0.5-2,0.3-3.1c-0.2-1-0.7-2-1.5-2.7C11.8,4.7,10.8,4.2,9.8,4c-1-0.2-2.1-0.1-3.1,0.3c-1,0.4-1.8,1.1-2.4,2
                                          C3.7,7.2,3.4,8.2,3.4,9.3L3.4,9.3z"/>
                            </g>
                          </svg>
                        </Button>
                        <Input
                          type="text"
                          className='border-0'
                          id="search_members"
                          name="search_members"
                          placeholder="Search members by name or email"
                          onChange={handleInputChange}
                          value={searchMembers}
                        />
                      </div>
                    </Form>
                  </div>
                  <div className='text-right' >
                    <Link to={`/teams/${localTeam.id}/add-member`} className="btn btn-green-light btn-sm">
                      <svg xmlns="http://www.w3.org/2000/svg" width={15} height={15} viewBox="0 0 15 15" fill='currentColor' className='mt-n1'>
                        <path d="M14.5,7.9c0,0.2-0.1,0.3-0.2,0.5c-0.1,0.1-0.3,0.2-0.5,0.2h-0.4V9c0,0.2-0.1,0.3-0.2,0.5
                        c-0.1,0.1-0.3,0.2-0.5,0.2c-0.2,0-0.3-0.1-0.5-0.2c-0.1-0.1-0.2-0.3-0.2-0.5V8.6h-0.4c-0.2,0-0.3-0.1-0.5-0.2
                        C11.1,8.3,11,8.1,11,7.9c0-0.2,0.1-0.3,0.2-0.5c0.1-0.1,0.3-0.2,0.5-0.2h0.4V6.8c0-0.2,0.1-0.3,0.2-0.5c0.1-0.1,0.3-0.2,0.5-0.2
                        c0.2,0,0.3,0.1,0.5,0.2c0.1,0.1,0.2,0.3,0.2,0.5v0.4h0.4c0.2,0,0.3,0.1,0.5,0.2C14.4,7.6,14.5,7.8,14.5,7.9z M11.5,11
                        c0.1,0.1,0.2,0.3,0.2,0.5c0,0.2-0.1,0.3-0.2,0.4c-0.1,0.1-0.3,0.2-0.5,0.2c-0.2,0-0.3-0.1-0.4-0.2c-0.7-0.9-2.1-2-4.1-2
                        c-1.6,0-3,0.7-4.1,2C2.2,12,2,12.1,1.9,12.1c-0.2,0-0.3,0-0.5-0.2c-0.1-0.1-0.2-0.3-0.2-0.4c0-0.2,0-0.3,0.2-0.5
                        C2,10.2,2.9,9.5,4,9.1c-0.6-0.5-1.1-1.2-1.3-2C2.4,6.3,2.4,5.5,2.7,4.7C3,3.9,3.5,3.2,4.1,2.8C4.8,2.3,5.6,2,6.4,2S8,2.3,8.7,2.8
                        c0.7,0.5,1.2,1.1,1.4,1.9c0.3,0.8,0.3,1.6,0.1,2.4c-0.2,0.8-0.7,1.5-1.3,2C9.9,9.5,10.8,10.2,11.5,11z M6.4,8.6c0.5,0,1-0.2,1.5-0.4
                        c0.4-0.3,0.8-0.7,1-1.2C9,6.5,9.1,6,9,5.5c-0.1-0.5-0.4-1-0.7-1.3C7.9,3.7,7.4,3.5,6.9,3.4c-0.5-0.1-1,0-1.5,0.1
                        c-0.5,0.2-0.9,0.5-1.2,1C3.9,4.9,3.8,5.4,3.8,6c0,0.7,0.3,1.4,0.8,1.9C5,8.3,5.7,8.6,6.4,8.6z"/>
                      </svg>
                      <span className='d-none d-md-inline-block pl-1'>Add Member</span>
                    </Link>
                  </div>
                </div>

                <div className="members-lists">
                  {filteredMembers.map((member, i) => {
                    return (
                      <MemberItem 
                        team_id={localTeam.id} 
                        key={`${member.id}-${i}`} 
                        member={member} 
                        isMember={true} 
                        isLeader={member.id == team_report.leader_id ? 1 : 0} 
                        onChangeLeaderMember={onChangeLeaderMember} 
                      />
                    );
                  })}
                </div>

                {displayLoadingIndicator && <LoadingIndicator />}
              </div>
            </CSSTransition>
          </TransitionGroup>
        </div>
      </div>
    </>
  );
};

export default EditPage;